import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl/dist/maplibre-gl-csp.js";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "../lib/api";
import Seo from "../lib/seo";

// CSP build + external worker file: the default maplibre build serializes its
// worker via Function.prototype.toString(), which webpack's production pipeline
// transforms — the serialized worker then fails at runtime (tiles never parse).
// Serving the worker as a static file sidesteps that entirely.
maplibregl.setWorkerUrl(`${window.location.origin}/maplibre-csp-worker.js`);

// Basemap served through our own domain (/api/map/* proxies CARTO Dark Matter)
// so client-side blockers/filters on the CARTO CDN can't strip the map.
const STYLE_URL = "/api/map/style.json";
const FALLBACK_CENTER = [77.209, 28.6139]; // Delhi
const FALLBACK_ZOOM = 11;

const isTouch = () => window.matchMedia("(hover: none)").matches;

export default function AllCity() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const previewRef = useRef(null);
  const centeredRef = useRef(false);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(null);
  const [records, setRecords] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [hoverRec, setHoverRec] = useState(null);
  const [tapRec, setTapRec] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);

  const positionPreview = (e) => {
    const p = previewRef.current;
    if (!p) return;
    const w = 208, pad = 28;
    let x = e.clientX + pad;
    let y = e.clientY - 60;
    if (x + w > window.innerWidth) x = e.clientX - w - pad;
    if (y < 8) y = 8;
    p.style.transform = `translate(${x}px, ${y}px)`;
  };

  const selectCity = (c, immediate = false) => {
    setCity(c);
    setSelectorOpen(false);
    setTapRec(null);
    api.get("/allcity/records", { params: { city_id: c.id } })
      .then((r) => setRecords(r.data))
      .catch(() => setRecords([]));
    if (mapRef.current && mapReady) {
      mapRef.current[immediate ? "jumpTo" : "flyTo"]({ center: [c.lng, c.lat], zoom: c.zoom || 12, speed: 1.1 });
      centeredRef.current = true;
    }
  };

  // initial load: published cities + backend-controlled default city
  useEffect(() => {
    api.get("/allcity").then((r) => {
      const cs = r.data?.cities || [];
      setCities(cs);
      const def = cs.find((c) => c.id === r.data?.default_city_id) || cs[0];
      if (def) selectCity(def, true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: FALLBACK_CENTER,
      zoom: FALLBACK_ZOOM,
      attributionControl: false,
      preserveDrawingBuffer: true,   // keep the frame readable for captures/testing
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    map.on("load", () => setMapReady(true));
    map.on("click", () => setTapRec(null));
    mapRef.current = map;
    window.__acmap = map;   // debug/testing handle
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // centre once both the map and the default city are available
  useEffect(() => {
    if (mapReady && city && !centeredRef.current && mapRef.current) {
      mapRef.current.jumpTo({ center: [city.lng, city.lat], zoom: city.zoom || 12 });
      centeredRef.current = true;
    }
  }, [mapReady, city]);

  // sticker markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    records.forEach((rec) => {
      const el = document.createElement("div");
      const found = rec.status === "found";
      el.className = "ac-marker" + (found ? " ac-marker--found" : "");
      el.setAttribute("data-testid", `marker-${rec.sticker_id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`);
      const dot = '<span class="ac-dot"></span>';
      if (found) {
        const d = (Math.random() * 1.3).toFixed(2);
        el.innerHTML =
          `<span class="ac-ripple" style="animation-delay:${d}s"></span>` +
          `<span class="ac-ripple" style="animation-delay:${(+d + 1.3).toFixed(2)}s"></span>` + dot;
        el.setAttribute("role", "button");
        el.setAttribute("tabIndex", "0");
        el.setAttribute("aria-label", `${rec.sticker_id} — found, open documentation`);
        el.addEventListener("mouseenter", (e) => {
          if (isTouch()) return;
          positionPreview(e);
          setHoverRec(rec);
        });
        el.addEventListener("mousemove", positionPreview);
        el.addEventListener("mouseleave", () => setHoverRec(null));
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          if (isTouch()) setTapRec(rec);
          else if (rec.practice_url) window.open(rec.practice_url, "_blank", "noopener,noreferrer");
        });
        el.addEventListener("keydown", (e) => {
          if ((e.key === "Enter" || e.key === " ") && rec.practice_url) {
            e.preventDefault();
            window.open(rec.practice_url, "_blank", "noopener,noreferrer");
          }
        });
      } else {
        el.innerHTML = dot;
      }
      markersRef.current.push(new maplibregl.Marker({ element: el }).setLngLat([rec.lng, rec.lat]).addTo(map));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, mapReady]);

  // preload preview images in the background so hovers are instant
  useEffect(() => {
    const t = setTimeout(() => {
      records.forEach((r) => { if (r.preview_image) { const img = new Image(); img.src = r.preview_image; } });
    }, 1200);
    return () => clearTimeout(t);
  }, [records]);

  const activeCities = cities.filter((c) => c.record_count > 0);

  return (
    <main data-testid="allcity-page" className="pt-28 md:pt-36">
      <Seo title="ALL CITY — RCKT"
        description="A nocturnal geographical record of RCKT field activity — stickers placed, and stickers found, across cities."
        canonical={`${window.location.origin}/all-city`} />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between border-b hairline pb-5 mb-6">
          <h1 className="label" data-testid="allcity-title">All City</h1>
          <div className="relative">
            <button data-testid="city-selector" onClick={() => setSelectorOpen((o) => !o)}
              className="label hover:opacity-50 transition-opacity">
              {city ? `${city.name}${city.country ? ` — ${city.country}` : ""}` : "—"} ↓
            </button>
            {selectorOpen && activeCities.length > 0 && (
              <div className="absolute right-0 top-full mt-3 z-30 min-w-[12rem] border hairline"
                style={{ background: "var(--surface)" }} data-testid="city-selector-menu">
                {activeCities.map((c) => (
                  <button key={c.id} data-testid={`city-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => selectCity(c)}
                    className="block w-full text-left label px-4 py-3 transition-colors hover:bg-[var(--paper)]"
                    style={{ opacity: city?.id === c.id ? 0.35 : 1 }}>
                    {c.name}{c.country ? ` — ${c.country}` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="relative border hairline overflow-hidden"
          style={{ height: "calc(100vh - 14rem)", minHeight: "460px", background: "#0a0a0a" }}
          data-testid="allcity-map">
          <div ref={containerRef} style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }} />
          {tapRec && (
            <div className="absolute left-4 bottom-4 z-20 w-48 p-3"
              style={{ background: "#0d0d0d", border: "1px solid #2a2a2a" }} data-testid="ac-tap-preview">
              <a href={tapRec.practice_url} target="_blank" rel="noopener noreferrer" data-testid="ac-tap-open">
                {tapRec.preview_image && <img src={tapRec.preview_image} alt={tapRec.sticker_id} className="w-full block" />}
                <span className="label block mt-2" style={{ color: "#9a9a9a" }}>{tapRec.sticker_id}</span>
                <span className="label mt-3 inline-block underline" style={{ color: "#F7F5F0" }}>View documentation ↗</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* cursor-following preview (desktop hover) */}
      <div ref={previewRef} className="ac-preview" data-testid="ac-hover-preview"
        style={{ opacity: hoverRec ? 1 : 0 }}>
        {hoverRec?.preview_image && <img src={hoverRec.preview_image} alt={hoverRec.sticker_id} />}
        <div className="label mt-2" style={{ color: "#9a9a9a" }}>{hoverRec?.sticker_id || ""}</div>
      </div>
    </main>
  );
}
